import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { medicalApi } from "../../services/medicalApi";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

function bucketFor(check, today) {
  if (check.status === "NOT_YET_ASSIGNED") return "notAssigned";
  if (check.status === "APPOINTMENT_SCHEDULED") return "scheduled";
  if (!check.next_due_date) return "notAssigned";
  const days = Math.floor((new Date(check.next_due_date) - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return "overdue";
  if (days === 0) return "today";
  if (days <= 7) return "within7";
  if (days <= 14) return "within14";
  if (days <= 30) return "within30";
  if (days <= 60) return "within60";
  if (days <= 90) return "within90";
  return "compliant";
}

export default function MedicalComplianceDashboardPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageMedicalChecks } = usePermissions();
  const [checks, setChecks] = useState([]);
  const [activeBucket, setActiveBucket] = useState(null);

  const BUCKETS = [
    { key: "overdue", label: t("mgmtMedical.bucketOverdue"), color: "bg-red-600" },
    { key: "today", label: t("mgmtMedical.bucketDueToday"), color: "bg-red-500" },
    { key: "within7", label: t("mgmtMedical.bucketWithin7"), color: "bg-orange-500" },
    { key: "within14", label: t("mgmtMedical.bucketWithin14"), color: "bg-amber-500" },
    { key: "within30", label: t("mgmtMedical.bucketWithin30"), color: "bg-yellow-500" },
    { key: "within60", label: t("mgmtMedical.bucketWithin60"), color: "bg-blue-400" },
    { key: "within90", label: t("mgmtMedical.bucketWithin90"), color: "bg-blue-300" },
    { key: "compliant", label: t("mgmtMedical.bucketCompliant"), color: "bg-emerald-500" },
    { key: "scheduled", label: t("mgmtMedical.bucketScheduled"), color: "bg-purple-400" },
    { key: "notAssigned", label: t("mgmtMedical.bucketNotAssigned"), color: "bg-gray-400" },
  ];

  useEffect(() => { if (canManageMedicalChecks) medicalApi.listChecks().then((r) => setChecks(r.data)).catch(() => {}); }, [canManageMedicalChecks]);

  if (!canManageMedicalChecks) return <p className="text-sm text-gray-500" data-testid="medical-compliance-access-denied">{t("common.accessDenied")}</p>;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const grouped = {};
  BUCKETS.forEach((b) => (grouped[b.key] = []));
  checks.forEach((c) => grouped[bucketFor(c, today)].push(c));

  const shown = activeBucket ? grouped[activeBucket] : [];
  const overdueCount = grouped.overdue.length;

  return (
    <div className="space-y-5" data-testid="medical-compliance-dashboard-page">
      <PageHelpBanner pageKey="medical-compliance-dashboard" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtMedical.dashboardHelp")}</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtMedical.dashboardTitle")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtMedical.dashboardSubtitle")}</p>
        </div>
        <button data-testid="open-medical-checks-table-btn" onClick={() => navigate("/management/medical-checks")} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 bg-white">{t("mgmtMedical.fullTableArrow")}</button>
      </div>

      {overdueCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="corrective-deadline-banner">
          <p className="text-sm font-semibold text-red-700">{overdueCount} {t("mgmtMedical.employeesOverdue")}</p>
          <p className="text-xs text-red-600 mt-1">{t("mgmtMedical.correctiveDeadlineNote")}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {BUCKETS.map((b) => (
          <button key={b.key} data-testid={`medical-bucket-${b.key}`} onClick={() => setActiveBucket(activeBucket === b.key ? null : b.key)}
            className={`bg-white border rounded-xl p-4 text-left transition-colors ${activeBucket === b.key ? "border-gray-900" : "border-gray-200 hover:border-gray-400"}`}>
            <div className={`w-2 h-2 rounded-full ${b.color} mb-2`} />
            <p className="text-2xl font-bold text-gray-900">{grouped[b.key].length}</p>
            <p className="text-xs text-gray-500">{b.label}</p>
          </button>
        ))}
      </div>

      {activeBucket && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="medical-bucket-detail-panel">
          <p className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-gray-500 border-b border-gray-100">{BUCKETS.find((b) => b.key === activeBucket).label}</p>
          <div className="divide-y divide-gray-100">
            {shown.map((c) => (
              <button key={c.employee_id} data-testid={`medical-bucket-employee-${c.employee_id}`} onClick={() => navigate(`/management/employees/${c.employee_id}`)}
                className="w-full text-left p-4 flex items-center justify-between hover:bg-gray-50">
                <span className="text-sm font-semibold text-gray-800">{c.employee_name}</span>
                <span className="text-xs text-gray-500">{c.next_due_date ? `${t("mgmtMedical.due")} ${c.next_due_date}` : t("mgmtMedical.noDueDateOnFile")}</span>
              </button>
            ))}
            {shown.length === 0 && <p className="p-6 text-center text-sm text-gray-500" data-testid="no-medical-bucket-employees">{t("mgmtMedical.noEmployeesInBucket")}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
